import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Download, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

// Reusable close workflow: enter amount → close → "Download PDF?" → "Schedule next?".
export function CloseFlow({ open, onOpenChange, title, suggested, initialAmount = "",
                           onConfirmClose, pdfUrl, onSchedule, scheduleLabel = "next appointment/session" }) {
  const [step, setStep] = useState("amount");
  const [amount, setAmount] = useState(initialAmount);
  const [busy, setBusy] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [nextDate, setNextDate] = useState("");

  useEffect(() => {
    if (open) { setStep("amount"); setAmount(initialAmount || suggested || ""); setRecordId(null); setNextDate(""); }
  }, [open, initialAmount, suggested]);

  const close = () => onOpenChange(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const id = await onConfirmClose(parseFloat(amount || 0));
      setRecordId(id);
      toast.success("Record closed");
      setStep("pdf");
    } catch (e) { console.error("Close record failed:", e); /* user-facing toast handled by caller */ }
    finally { setBusy(false); }
  };

  const doSchedule = async () => {
    if (!nextDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try { await onSchedule(nextDate); toast.success("Next session scheduled"); close(); }
    catch (e) { console.error("Schedule next session failed:", e); /* user-facing toast handled by caller */ }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        {step === "amount" && (
          <>
            <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
            <Label>Final amount paid (₹)</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="close-amount-input" autoFocus />
            {suggested ? <p className="text-xs text-muted-foreground">Suggested (age-based): ₹{suggested}</p> : null}
            <Button onClick={confirm} disabled={busy} data-testid="confirm-close-btn" className="w-full mt-2"><CheckCircle2 className="h-4 w-4 mr-2" />Close Record</Button>
          </>
        )}
        {step === "pdf" && (
          <>
            <DialogHeader><DialogTitle>Download the PDF?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">The record is closed. Would you like to open the PDF report?</p>
            <div className="flex gap-2 mt-2">
              <Button className="flex-1" data-testid="close-pdf-yes" onClick={() => { if (recordId && pdfUrl) window.open(pdfUrl(recordId), "_blank"); setStep(onSchedule ? "schedule" : "done"); if (!onSchedule) close(); }}>
                <Download className="h-4 w-4 mr-2" />Yes
              </Button>
              <Button variant="outline" className="flex-1" data-testid="close-pdf-no" onClick={() => { if (onSchedule) setStep("schedule"); else close(); }}>No</Button>
            </div>
          </>
        )}
        {step === "schedule" && (
          <>
            <DialogHeader><DialogTitle>Schedule the {scheduleLabel}?</DialogTitle></DialogHeader>
            <Label>Date & time</Label>
            <Input type="datetime-local" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="close-next-date" />
            <div className="flex gap-2 mt-2">
              <Button className="flex-1" onClick={doSchedule} disabled={busy} data-testid="close-schedule-yes"><CalendarPlus className="h-4 w-4 mr-2" />Yes, schedule</Button>
              <Button variant="outline" className="flex-1" onClick={close} data-testid="close-schedule-not-now">Not now</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
